import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/ModelEvaluator.css';
import '../styles/TokenUsage.css';

function ModelEvaluator() {
  const [models, setModels] = useState([]);
  const [testQuestions, setTestQuestions] = useState('');
  const [useCases, setUseCases] = useState([
    { id: 'general_qa', label: 'General Q&A', checked: true },
    { id: 'document_search', label: 'Document Search', checked: true },
    { id: 'code_generation', label: 'Code Generation', checked: false },
    { id: 'summarization', label: 'Summarization', checked: false },
    { id: 'creative_writing', label: 'Creative Writing', checked: false }
  ]);
  const [evaluationResults, setEvaluationResults] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState('document_search');
  const [priority, setPriority] = useState('balanced');
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('evaluate');

  useEffect(() => {
    // Fetch available models when component mounts
    fetchModels();
    fetchExistingEvaluations();
  }, []);

  const fetchModels = async () => {
    try {
      const data = await apiService.getAvailableModels();
      setModels(data.available_models || []);
    } catch (error) {
      console.error('Error fetching models:', error);
      setError('Failed to load models. Please try again later.');
    }
  };

  const fetchExistingEvaluations = async () => {
    try {
      const data = await apiService.getModelEvaluations();
      if (data && Object.keys(data).length > 0) {
        setEvaluationResults(data);
      }
    } catch (error) {
      console.error('Error fetching existing evaluations:', error);
      // Don't set error state here as this is just a supplementary fetch
    }
  };

  const handleEvaluate = async () => {
    if (!testQuestions.trim()) {
      setError('Please enter at least one test question');
      return;
    }

    try {
      setError(null);
      setIsEvaluating(true);
      
      // Parse test questions into an array
      const questions = testQuestions.split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);
      
      // Get selected use cases
      const selectedUseCases = useCases
        .filter(uc => uc.checked)
        .map(uc => uc.id);
      
      // Call the API to evaluate models
      const results = await apiService.evaluateModels(questions, selectedUseCases);
      setEvaluationResults(results);
      setIsEvaluating(false);
    } catch (error) {
      console.error('Error evaluating models:', error);
      setError('Failed to evaluate models. Please try again later.');
      setIsEvaluating(false);
    }
  };
  const handleGetRecommendation = async () => {
    try {
      setError(null);
      const result = await apiService.getModelRecommendation(selectedUseCase, priority);
      setRecommendation(result);
      
      // Automatically refresh the model list since evaluations could have changed
      fetchModels();
    } catch (error) {
      console.error('Error getting recommendation:', error);
      if (error.response && error.response.data.status === 'needs_evaluation') {
        setError('Please run a model evaluation first before getting recommendations.');
      } else {
        setError('Failed to get model recommendation. Please try again later.');
      }
    }
  };

  const handleUseCaseChange = (e) => {
    setSelectedUseCase(e.target.value);
    // Clear recommendation when use case changes
    setRecommendation(null);
  };

  const handlePriorityChange = (e) => {
    setPriority(e.target.value);
    // Clear recommendation when priority changes
    setRecommendation(null);
  };

  const handleCheckboxChange = (id) => {
    setUseCases(useCases.map(uc => 
      uc.id === id ? { ...uc, checked: !uc.checked } : uc
    ));
  };  const handleSwitchToModel = async (modelId) => {
    try {
      setError(null);
      await apiService.switchModel(modelId);
      
      // Update the evaluation results to show the correct model as active
      if (window.dispatchEvent) {
        // Dispatch a custom event that ModelSelector can listen for
        const event = new CustomEvent('modelChanged', { detail: { model: modelId } });
        window.dispatchEvent(event);
      }
      
      // Refresh the models list
      fetchModels();
      
      alert(`Successfully switched to ${modelId} model`);
    } catch (error) {
      console.error('Error switching model:', error);
      setError(`Failed to switch to ${modelId} model. Please try again.`);
    }
  };

  const formatCost = (cost) => {
    if (cost === 0) return 'Free';
    if (cost < 0.01) return '<$0.01';
    return `$${cost.toFixed(3)}`;
  };
  const formatLatency = (latency) => {
    return `${latency.toFixed(2)}s`;
  };

  const formatTokens = (count) => {
    if (!count && count !== 0) return 'N/A';
    if (count === 0) return '0';
    if (count < 1000) return count.toString();
    return `${(count / 1000).toFixed(1)}K`;
  };

  const getModelIcon = (modelId) => {
    switch (modelId) {
      case 'openai': return 'auto_awesome';
      case 'azure_openai': return 'cloud';
      case 'gemini': return 'psychology';
      case 'transformers': return 'memory';
      default: return 'smart_toy';
    }
  };

  return (
    <div className="model-evaluator">
      <div className="evaluator-tabs">
        <button 
          className={`tab-button ${activeTab === 'evaluate' ? 'active' : ''}`}
          onClick={() => setActiveTab('evaluate')}
        >
          Evaluate Models
        </button>
        <button 
          className={`tab-button ${activeTab === 'recommend' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommend')}
        >
          Get Recommendations
        </button>
        <button 
          className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          Evaluation Results
        </button>
      </div>

      <div className="evaluator-content">
        {activeTab === 'evaluate' && (
          <div className="evaluate-tab">
            <h3>Evaluate Model Performance</h3>
            <p className="info-text">
              Test your models with sample questions to evaluate their performance, cost, and suitability for different use cases.
            </p>
            
            <div className="test-questions-section">
              <label>Test Questions:</label>
              <textarea 
                placeholder="Enter test questions (one per line) that you want to evaluate across all models..."
                value={testQuestions}
                onChange={(e) => setTestQuestions(e.target.value)}
                rows={5}
              />
            </div>
            
            <div className="use-cases-section">
              <label>Use Cases to Evaluate:</label>
              <div className="use-cases-checkboxes">
                {useCases.map(useCase => (
                  <div key={useCase.id} className="use-case-checkbox">
                    <input
                      type="checkbox"
                      id={useCase.id}
                      checked={useCase.checked}
                      onChange={() => handleCheckboxChange(useCase.id)}
                    />
                    <label htmlFor={useCase.id}>{useCase.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                <span>{error}</span>
              </div>
            )}
            
            <button 
              className="evaluate-button"
              onClick={handleEvaluate}
              disabled={isEvaluating}
            >
              {isEvaluating ? (
                <>
                  <span className="material-icons spinning">sync</span>
                  Evaluating...
                </>
              ) : (
                <>
                  <span className="material-icons">assessment</span>
                  Evaluate Models
                </>
              )}
            </button>
          </div>
        )}

        {activeTab === 'recommend' && (
          <div className="recommend-tab">
            <h3>Get Model Recommendations</h3>
            <p className="info-text">
              Get personalized model recommendations based on your specific use case and priorities.
            </p>
            
            <div className="recommendation-options">
              <div className="option-group">
                <label>Use Case:</label>
                <select value={selectedUseCase} onChange={handleUseCaseChange}>
                  <option value="general_qa">General Q&A</option>
                  <option value="document_search">Document Search</option>
                  <option value="code_generation">Code Generation</option>
                  <option value="summarization">Summarization</option>
                  <option value="creative_writing">Creative Writing</option>
                </select>
              </div>
              
              <div className="option-group">
                <label>Priority:</label>
                <select value={priority} onChange={handlePriorityChange}>
                  <option value="balanced">Balanced</option>
                  <option value="performance">Performance</option>
                  <option value="cost">Cost</option>
                </select>
              </div>
            </div>
            
            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                <span>{error}</span>
              </div>
            )}
            
            <button 
              className="recommend-button"
              onClick={handleGetRecommendation}
            >
              <span className="material-icons">recommend</span>
              Get Recommendation
            </button>
            
            {recommendation && (
              <div className="recommendation-result">
                <h4>Recommended Model</h4>
                <div className="recommended-model">
                  <div className="model-icon">
                    <span className="material-icons">{getModelIcon(recommendation.recommended_model)}</span>
                  </div>
                  <div className="model-details">
                    <div className="model-name">{recommendation.model_name}</div>
                    <div className="recommendation-reason">{recommendation.recommendation_reason}</div>
                    
                    {recommendation.full_model_id && recommendation.full_model_id !== recommendation.recommended_model && (
                      <div className="model-config">
                        <span className="config-label">Configuration ID:</span>
                        <span className="config-value">{recommendation.full_model_id}</span>
                      </div>
                    )}
                    
                    {recommendation.evaluation_data && recommendation.evaluation_data.performance && (
                      <div className="model-metrics">
                        <div className="metric">
                          <span className="metric-label">Success Rate:</span>
                          <span className="metric-value">
                            {(recommendation.evaluation_data.performance.success_rate * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Avg. Latency:</span>
                          <span className="metric-value">
                            {formatLatency(recommendation.evaluation_data.performance.avg_latency)}
                          </span>
                        </div>                        <div className="metric">
                          <span className="metric-label">Cost:</span>
                          <span className="metric-value">
                            {formatCost(recommendation.evaluation_data.performance.cost)}
                          </span>
                        </div>
                        {recommendation.evaluation_data.performance.token_usage && (
                          <div className="metric">
                            <span className="metric-label">Token Usage:</span>
                            <span className="metric-value token-details">                              <span className="token-total">
                                {formatTokens(recommendation.evaluation_data.performance.token_usage.input + 
                                  recommendation.evaluation_data.performance.token_usage.output)} tokens total
                              </span>
                              <span className="token-breakdown">
                                In: {formatTokens(recommendation.evaluation_data.performance.token_usage.input)} / 
                                Out: {formatTokens(recommendation.evaluation_data.performance.token_usage.output)}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="switch-model">
                    <button 
                      className="switch-button"
                      onClick={() => handleSwitchToModel(recommendation.recommended_model)}
                    >
                      Switch to this model
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="results-tab">
            <h3>Evaluation Results</h3>
            {Object.keys(evaluationResults).length === 0 ? (
              <p className="no-results">No evaluation results available. Run an evaluation first.</p>
            ) : (
              <div className="results-container">                <div className="results-header">
                  <div className="header-cell">Model</div>
                  <div className="header-cell">Latency</div>
                  <div className="header-cell">Success Rate</div>
                  <div className="header-cell">Cost</div>
                  <div className="header-cell">Token Usage</div>
                </div>
                
                {Object.entries(evaluationResults).map(([modelId, data]) => {
                  if (data.error) return null;
                  
                  const modelName = data.model_name || 'Unknown';
                  const performance = data.performance || {};
                  const configName = data.config_name || '';
                  const tokenUsage = performance.token_usage || { input: 0, output: 0 };
                  const totalTokens = tokenUsage.input + tokenUsage.output;
                  
                  // Extract platform for UI display
                  const platform = modelId.split('-')[0] || modelId;
                  
                  return (
                    <div key={modelId} className="result-row">
                      <div className="model-cell">
                        <span className="material-icons">{getModelIcon(platform)}</span>
                        <div className="model-info">
                          <div className="model-provider">{platform}</div>
                          <div className="model-name">
                            {modelName}
                            {configName && ` (${configName})`}
                          </div>
                        </div>
                      </div>
                      <div className="metric-cell">
                        {performance.avg_latency ? formatLatency(performance.avg_latency) : 'N/A'}
                      </div>
                      <div className="metric-cell">
                        {performance.success_rate !== undefined ? 
                          `${(performance.success_rate * 100).toFixed(1)}%` : 'N/A'}
                      </div>
                      <div className="metric-cell">
                        {performance.cost !== undefined ? formatCost(performance.cost) : 'N/A'}
                      </div>                      <div className="metric-cell token-usage-cell">
                        <div className="token-details">
                          <span className="token-total">{formatTokens(totalTokens)} tokens</span>
                          <span className="token-breakdown">In: {formatTokens(tokenUsage.input)} / Out: {formatTokens(tokenUsage.output)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ModelEvaluator;